/** Which files of a repository count as code (the code reading round, 2026-11-04; brief part three, the red team's findings 6 and 7): one
 *  pure classifier the investigation's tree, its local copy and the connector's code tools share. A file is *kept* (code and text of any
 *  language, which the AI may read) or *skipped* with a reason in plain words, the words the Knowledge › Sources row shows. The order:
 *  secrets, then folders, then the file's kind, then its size. PHP and other server code are kept even inside a build folder (WordPress
 *  themes keep PHP anywhere), and a folder rule is lifted for a release when it would drop ten times the code the rest keeps (a theme
 *  published from `dist/`). Nothing here reads a disk or the network. */

/** a secret file (M5 brief, task 3): `.env*`, `*.pem`, `*.key`, in any folder; never listed, read, searched or written (moved here from the
 *  server's secret filter, which re-exports it, so the classifier can name secrets first) */
export const isSecretFile = (path: string): boolean => {
  const name = path.split("/").pop() ?? path;
  return /^\.env(\..+)?$/i.test(name) || /\.(pem|key)$/i.test(name);
};

/** one file over this is skipped as "over 1 MB" (bundles, dumps, generated data) */
export const CODE_FILE_MAX_BYTES = 1024 * 1024;
/** the local copy's limits: kept text, kept files, the download's time (Q2; the 150 MB packed cap is gone, Q6) */
export const CODE_COPY_LIMITS = Object.freeze({ keptBytes: 300 * 1024 * 1024, keptFiles: 50_000, downloadMs: 180_000 });

export type CodeSkipReason =
  | "secrets" | "dependencies" | "build output" | "editor settings" | "compiled files" | "images" | "fonts" | "audio and video" | "archives"
  | "documents" | "design files" | "translations" | "minified" | "lock files" | "system files" | "symbolic links" | "over 1 MB";
export type CodeVerdict = { kept: true } | { kept: false; reason: CodeSkipReason };

const FOLDERS: Record<string, CodeSkipReason> = {
  node_modules: "dependencies", vendor: "dependencies", bower_components: "dependencies",
  dist: "build output", build: "build output",
  ".idea": "editor settings", ".vscode": "editor settings",
  __pycache__: "compiled files", ".git": "system files",
};
/** the folder rules a release may lift when they would drop its real code */
const LIFTABLE: readonly CodeSkipReason[] = ["build output", "dependencies"];
const KINDS: [CodeSkipReason, readonly string[]][] = [
  ["images", ["png", "jpg", "jpeg", "gif", "webp", "svg", "ico", "bmp", "tif", "tiff", "avif", "heic"]],
  ["fonts", ["woff", "woff2", "ttf", "otf", "eot"]],
  ["audio and video", ["mp3", "mp4", "wav", "ogg", "oga", "webm", "mov", "avi", "m4a", "m4v", "flac", "aac", "mkv"]],
  ["archives", ["zip", "tar", "gz", "tgz", "bz2", "xz", "7z", "rar"]],
  ["documents", ["pdf", "doc", "docx", "xls", "xlsx", "ppt", "pptx", "odt", "ods"]],
  ["design files", ["psd", "ai", "sketch", "fig", "xd", "eps", "indd"]],
  ["translations", ["po", "pot", "mo"]],
  ["minified", ["map"]],
  ["compiled files", ["pyc", "class", "wasm", "o", "so", "dll", "exe", "jar", "dylib"]],
];
const BY_EXTENSION = new Map(KINDS.flatMap(([reason, exts]) => exts.map((e) => [e, reason] as const)));
const LOCK_FILES = new Set(["package-lock.json", "composer.lock", "yarn.lock", "pnpm-lock.yaml", "gemfile.lock", "cargo.lock", "poetry.lock"]);
const SYSTEM_FILES = new Set([".ds_store", "thumbs.db", "desktop.ini"]);
/** server code: kept even inside dist/ or build/ */
const SERVER_CODE = new Set(["php", "phtml", "py", "rb", "go", "java", "cs", "rs"]);
/** what "code" means for the folder lift: a programming or stylesheet language */
const CODE = new Set([...SERVER_CODE, "js", "mjs", "cjs", "jsx", "ts", "tsx", "vue", "svelte", "css", "scss", "sass", "less", "c", "h", "cpp", "hpp", "cc", "swift", "kt", "scala", "sh", "pl", "lua"]);

const extensionOf = (name: string): string => { const dot = name.lastIndexOf("."); return dot > 0 ? name.slice(dot + 1).toLowerCase() : ""; };
const nameOf = (path: string): string => path.split("/").pop() ?? path;
/** whether a path's extension names a programming or stylesheet language */
export const isCodePath = (path: string): boolean => CODE.has(extensionOf(nameOf(path)));

/** the folder rule that drops a path, if any (the first folder of the path that a rule names) */
function folderReason(path: string): CodeSkipReason | null {
  const folders = path.split("/").slice(0, -1);
  for (const f of folders) { const r = FOLDERS[f.toLowerCase()]; if (r) return r; }
  return null;
}
function kindReason(name: string): CodeSkipReason | null {
  const lower = name.toLowerCase();
  if (SYSTEM_FILES.has(lower)) return "system files";
  if (LOCK_FILES.has(lower)) return "lock files";
  if (/\.min\.(js|css)$/.test(lower)) return "minified";
  return BY_EXTENSION.get(extensionOf(name)) ?? null;
}

/** One file's verdict: secrets, then the folder (PHP and other server code never count as build output), then the kind, then the size;
 *  a symbolic link (GitHub's mode 120000, when the caller has it) is skipped too. `lift` names folder rules lifted for the release. */
export function classifyCodePath(path: string, size: number, mode?: string, lift: readonly CodeSkipReason[] = []): CodeVerdict {
  const name = nameOf(path);
  if (isSecretFile(path)) return { kept: false, reason: "secrets" };
  if (mode === "120000") return { kept: false, reason: "symbolic links" };
  const folder = folderReason(path);
  if (folder && !lift.includes(folder) && !(folder === "build output" && SERVER_CODE.has(extensionOf(name)))) return { kept: false, reason: folder };
  const kind = kindReason(name);
  if (kind) return { kept: false, reason: kind };
  if (size > CODE_FILE_MAX_BYTES) return { kept: false, reason: "over 1 MB" };
  return { kept: true };
}

export type CodeEntry = { path: string; size: number; mode?: string };
export type CodeSkipGroup = { reason: CodeSkipReason; files: number; bytes: number };
export type ClassifiedRelease = { kept: { path: string; size: number }[]; keptBytes: number; skipped: CodeSkipGroup[]; skippedFiles: number; lifted: CodeSkipReason[] };

/** the folder rules to lift: those whose dropped code is more than ten times the code the rules keep */
function liftsFor(entries: readonly CodeEntry[]): CodeSkipReason[] {
  let keptCode = 0;
  const dropped = new Map<CodeSkipReason, number>();
  for (const e of entries) {
    if (!isCodePath(e.path)) continue;
    const v = classifyCodePath(e.path, e.size, e.mode);
    if (v.kept) keptCode += e.size;
    else if (LIFTABLE.includes(v.reason) && classifyCodePath(e.path, e.size, e.mode, [v.reason]).kept) dropped.set(v.reason, (dropped.get(v.reason) ?? 0) + e.size);
  }
  return LIFTABLE.filter((r) => (dropped.get(r) ?? 0) > 0 && keptCode * 10 < dropped.get(r)!);
}

/** The whole release at once: the kept files with their bytes, the skipped ones grouped by reason (most files first), and the folder
 *  rules lifted for it. */
export function classifyRelease(entries: readonly CodeEntry[]): ClassifiedRelease {
  const lifted = liftsFor(entries);
  const kept: { path: string; size: number }[] = [];
  const groups = new Map<CodeSkipReason, CodeSkipGroup>();
  let keptBytes = 0;
  for (const e of entries) {
    const v = classifyCodePath(e.path, e.size, e.mode, lifted);
    if (v.kept) { kept.push({ path: e.path, size: e.size }); keptBytes += e.size; continue; }
    const g = groups.get(v.reason) ?? { reason: v.reason, files: 0, bytes: 0 };
    g.files += 1; g.bytes += e.size; groups.set(v.reason, g);
  }
  const skipped = [...groups.values()].sort((a, b) => b.files - a.files || b.bytes - a.bytes);
  return { kept, keptBytes, skipped, skippedFiles: skipped.reduce((n, g) => n + g.files, 0), lifted };
}
